import { enqueueSnackbar } from '~/utils/notifications';
import { useCallback, useState } from 'react';
import { useIntl } from 'react-intl';
import { useSessionOrNull } from '~/contexts/SessionContext';
import { DicomColor } from '~/domain/dicom/dicomColor';
import { RoiGenerationAlgorithmType } from '~/domain/dicom/dicomstructureset';
import {
    BODY_CONTOUR_OPERATION,
    domainEventBus,
    RoiColorChangeRequestedEvent,
} from '~/domain/events';
import { Image3D } from '~/domain/patient/image3d';
import { ContourTexture } from '~/engine/data/structure/ContourTexture';
import { RegionOfInterest } from '~/engine/data/structure/RegionOfInterest';
import { Environment } from '~/engine/data/Environment';
import { bodyContourTextureFromMask } from '~/engine/util/algorithms/sdf/BodyContourExtraction';
import { getSigmaForStrength } from '~/engine/util/algorithms/sdf/GaussianSmoothing';
import { getErrorMessage } from '~/utils/errors';
import { mm2ToCm2, mmToCm } from '~/utils/units';
import { waitForUI } from '~/utils/uiUtils';
import { rollbackCreatedRois } from '~/hooks/structure-set/rollbackCreatedRois';
import { useGetOrCreateDestination } from '~/hooks/structure-set/useGetOrCreateDestination';
import {
    BodyContourMaskCache,
    BodyContourParams,
    THRESHOLD_TOOL_HU_ELIGIBILITY,
    ThresholdToolEligibility,
    resolveKeepInsideTexture,
    toBodyContourTextureStages,
} from './bodyContourParams';
import { ThresholdSegmentationPreset } from './thresholdSegmentationPresets';

/**
 * The slice of a preset the apply path actually consumes: the output's
 * typing/provenance. Structural, so the CT presets pass their full
 * {@link ThresholdSegmentationPreset} unchanged while the SUV tool passes
 * its own small output config.
 */
export interface ThresholdOutputPreset {
    output: Pick<
        ThresholdSegmentationPreset['output'],
        'interpretedType' | 'identificationCode'
    >;
}

/**
 * Length budget of the generation description: it is stamped on DICOM
 * (3006,0036) ROI Generation Description, whose value representation is
 * LO — 64 characters, ASCII.
 */
const GENERATION_DESCRIPTION_MAX_CHARS = 64;

/** Marks a record whose lowest-priority tokens did not fit. */
const GENERATION_DESCRIPTION_TRUNCATION_MARKER = '~';

/**
 * Compact deterministic ASCII number: at most two decimals, no trailing
 * zeros, so the same parameters always render the same token.
 */
function describeNumber(value: number): string {
    return String(Number(value.toFixed(2)));
}

/**
 * Join the record within the DICOM LO budget. Tokens are emitted in
 * priority order (see {@link describeHuGeneration}), so an overflow —
 * only reachable with extreme user values — drops trailing tokens, the
 * fine cleanup detail, and never the threshold or the extent flags. The
 * dropped tail is DECLARED with a trailing marker rather than silently
 * cut, so a reader can tell a short record from a truncated one.
 */
function fitGenerationDescription(tokens: string[]): string {
    const full = tokens.join(' ');
    if (full.length <= GENERATION_DESCRIPTION_MAX_CHARS) {
        return full;
    }
    const kept = [...tokens];
    while (
        kept.length > 1 &&
        [...kept, GENERATION_DESCRIPTION_TRUNCATION_MARKER].join(' ').length >
            GENERATION_DESCRIPTION_MAX_CHARS
    ) {
        kept.pop();
    }
    return [...kept, GENERATION_DESCRIPTION_TRUNCATION_MARKER].join(' ');
}

/**
 * The CT tool's provenance record — the default description builder,
 * stamped on the output structure (create AND overwrite paths).
 *
 * It must let a reviewer reconstruct WHAT the user acted on, so every
 * parameter that shaped the geometry is recorded, not just the threshold.
 * Space-separated, self-identifying tokens in a FIXED order:
 *
 * - `HU>=<hu>`   threshold, inclusive — a voxel AT the threshold is body
 * - `HU>=<lo><=<hi>` a BANDED preset's threshold (Bone): both bounds
 *                inclusive, exactly as the mask sampler applies them.
 *                A single-sided run emits the plain `HU>=<hu>` form, so
 *                its record is byte-identical to what it was before
 *                banding existed
 * - `box`        the run was restricted to the region box
 * - `clip`       the result was intersected with the EXTERNAL (keep-inside)
 * - `fill`       per-slice cavity fill ran in the mask phase
 * - `sm<s>vox`   smoothing, with the Gaussian sigma it ran at
 * - `min<a>cm2`  min-part floor, per-slice in-plane area
 * - `k2D<n>`     keep the n largest parts per slice
 * - `k3D<n>`     keep the n largest 3D parts
 * - `ff`         final per-slice cavity re-fill after the cleanup stages
 * - `mgn<d>cm`   symmetric margin
 *
 * Only stages that were ON appear; the threshold is always present. The
 * two extent flags follow it directly — they change the result's extent
 * far more than the cleanup stages do, so they are the last to be dropped
 * if the record ever has to be truncated. Values carry the unit the panel
 * showed them in (cm, cm²), so the record cannot be read in the wrong one.
 *
 * Smoothing is the one parameter the panel deliberately does NOT number:
 * its control is a continuous light-to-strong slider, because a strength
 * index is not something a clinician can judge a contour against. The
 * record therefore carries the PHYSICAL parameter that shaped the
 * geometry — the Gaussian sigma the stage ran at — rather than the UI
 * position, so a reviewer can reconstruct the run without the panel's
 * scale. Rounded to two decimals like every other value here, which is
 * lossless for the reachable set (the position is 0.1-quantized, and the
 * sigma is a smooth function of it). Its unit is the SOURCE IMAGE's
 * in-plane (X) voxel size — the smoothing kernel is defined on the voxel
 * grid, so sigma × in-plane spacing is the physical extent in mm — hence
 * the `vox` suffix rather than `mm`.
 *
 * Examples: `HU>=-300 box clip fill sm1.9vox min5cm2 k2D3 k3D1 ff mgn0.5cm`
 * (strength position 6 — the Body default — maps to sigma 1.9), and
 * `HU>=150<=2000 clip sm0.7vox min0.5cm2` (the Bone defaults, whose
 * threshold is a band).
 *
 * The record fits the 64-character budget for every reachable parameter
 * set of every preset — re-derived per preset by unit tests, the widest
 * being High density at 58 characters. A banded threshold token is at
 * most `HU>=1000<=3000` (14 characters, Bone's widest), 5 more than the
 * single-sided form it replaces on that preset.
 */
function describeHuGeneration(params: BodyContourParams): string {
    // Both bounds inclusive, matching the mask sampler. An unbounded run
    // (absent bound, or +Infinity) emits the historical single-sided
    // token — a record that changed shape for an unchanged Body run
    // would make two identical generations look different.
    const upper = params.upperThresholdHu;
    const band =
        upper === undefined || upper === Number.POSITIVE_INFINITY
            ? ''
            : `<=${describeNumber(upper)}`;
    const tokens: string[] = [
        `HU>=${describeNumber(params.thresholdHu)}${band}`,
    ];
    // The user's setting, as configured: a box covering the whole volume
    // restricts nothing, but "Restrict to region" was on for this run.
    if (params.boundsMm !== null) {
        tokens.push('box');
    }
    if (params.keepInsideExternal !== null) {
        tokens.push('clip');
    }
    if (params.fillHoles2dEnabled) {
        tokens.push('fill');
    }
    if (params.smoothingEnabled) {
        tokens.push(
            `sm${describeNumber(getSigmaForStrength(params.smoothingStrength))}vox`
        );
    }
    if (params.minPartSizeEnabled) {
        tokens.push(`min${describeNumber(mm2ToCm2(params.minPartAreaMm2))}cm2`);
    }
    if (params.keepLargest2dEnabled) {
        tokens.push(`k2D${describeNumber(params.keepLargest2dCount)}`);
    }
    if (params.keepLargest3dEnabled) {
        tokens.push(`k3D${describeNumber(params.keepLargest3dCount)}`);
    }
    if (params.finalFillHoles2dEnabled) {
        tokens.push('ff');
    }
    if (params.marginMm > 0) {
        tokens.push(`mgn${describeNumber(mmToCm(params.marginMm))}cm`);
    }
    return fitGenerationDescription(tokens);
}

interface UseBodyContourApplicationOptions {
    environment: Environment | null;
    image: Image3D | undefined;
    onSuccess: () => void;
    updateView: () => void;
    /** Hand off the cached preview texture instead of recomputing. */
    consumePreview?: (params: BodyContourParams) => ContourTexture | undefined;
    /** Shared with the preview hook: a recompute on apply (no matching
     *  preview held) still reuses the cached CPU mask when the mask-stage
     *  parameters are unchanged. */
    maskCache: BodyContourMaskCache;
    /** Voxel-unit gate + refusal message. Defaults to the CT tool's HU
     *  gate; the SUV tool passes its SUVbw gate. Must match the
     *  maskCache's quantification — the cache is the typed backstop. */
    eligibility?: ThresholdToolEligibility;
    /** Generation-description builder stamped on the output (create AND
     *  overwrite paths). Defaults to the CT tool's parameter record (see
     *  {@link describeHuGeneration}). A caller-supplied builder must
     *  derive from the same render as the params it will be called with,
     *  so the record and the geometry cannot disagree — and must respect
     *  the same DICOM LO budget ({@link GENERATION_DESCRIPTION_MAX_CHARS}
     *  characters, ASCII). */
    describeGeneration?: (params: BodyContourParams) => string;
}

export function useBodyContourApplication({
    environment,
    image,
    onSuccess,
    updateView,
    consumePreview,
    maskCache,
    eligibility = THRESHOLD_TOOL_HU_ELIGIBILITY,
    describeGeneration = describeHuGeneration,
}: UseBodyContourApplicationOptions) {
    const intl = useIntl();
    const session = useSessionOrNull();
    const [isProcessing, setIsProcessing] = useState(false);
    const { getOrCreateDestination } = useGetOrCreateDestination({
        environment,
    });

    const handleApply = useCallback(
        async (
            params: BodyContourParams,
            preset: ThresholdOutputPreset,
            outputRoiNumber: number | null,
            color: DicomColor,
            newName?: string
        ) => {
            if (isProcessing) {
                enqueueSnackbar(
                    intl.formatMessage({ id: 'thresholdSeg.inProgress' }),
                    { variant: 'warning' }
                );
                return;
            }
            if (!environment?.structureSet || !image || !session) {
                return;
            }
            if (!eligibility.isQuantified(image)) {
                // Defense in depth behind the toolbar/panel gating —
                // reachable only through stale state (the image switched
                // to an ineligible one under an in-flight interaction).
                // SURFACED, not a silent return: applying must never
                // quietly do nothing, and the mask cache backs this up
                // with the tool's typed domain error on the recompute
                // path.
                enqueueSnackbar(
                    intl.formatMessage({ id: eligibility.refusalMessageId }),
                    { variant: 'error' }
                );
                return;
            }

            setIsProcessing(true);
            // Ownership marker for the release-with-acquisition rule: set
            // as soon as a texture is held, cleared conceptually when
            // replaceTexture hands it to the ROI (checked in the catch).
            let ownedTexture: ContourTexture | undefined;
            let dest: RegionOfInterest | null = null;
            // ROI number of a structure THIS apply created, cleared once
            // the apply has fully succeeded. Everything after the
            // creation (the texture hand-off, the tracked provenance
            // write) can throw, and a failure there would otherwise leave
            // a brand-new empty structure stamped AUTOMATIC behind the
            // error snackbar — see the rollback in the catch. Never set
            // on the overwrite path: a pre-existing destination is the
            // user's structure and is never deleted.
            let createdRoiNumber: number | null = null;
            try {
                // Texture FIRST, destination second: no ROI is created (or
                // touched) until a committable texture exists, so a GPU
                // failure cannot leave an empty EXTERNAL structure behind.
                let texture = consumePreview?.(params);
                if (!texture) {
                    enqueueSnackbar(
                        intl.formatMessage({ id: 'thresholdSeg.applying' }),
                        { variant: 'info' }
                    );
                    await waitForUI();
                    const mask = await maskCache.getOrExtract(image, params);
                    if (mask.isEmpty) {
                        // A user-parameter empty result (box off the
                        // patient, extreme threshold) must never replace
                        // the output's contours: refuse before any
                        // texture or destination exists, so nothing is
                        // created, touched, or announced as success.
                        // (The preview path never publishes an empty
                        // preview, so consumePreview cannot hand one in.)
                        enqueueSnackbar(
                            intl.formatMessage({
                                id: 'thresholdSeg.emptyResult',
                            }),
                            { variant: 'error' }
                        );
                        return;
                    }
                    texture = await bodyContourTextureFromMask(
                        image.grid,
                        mask,
                        {
                            ...toBodyContourTextureStages(params),
                            keepInsideTexture: resolveKeepInsideTexture(
                                environment,
                                params
                            ),
                        }
                    );
                }
                if (!texture) {
                    enqueueSnackbar(
                        intl.formatMessage(
                            { id: 'thresholdSeg.failed' },
                            {
                                error: intl.formatMessage({
                                    id: 'thresholdSeg.gpuUnavailable',
                                }),
                            }
                        ),
                        { variant: 'error' }
                    );
                    return;
                }
                if (texture.isEmpty) {
                    // The pipeline can empty a non-empty mask (the
                    // keep-inside clip with a disjoint EXTERNAL): refuse
                    // before any destination exists, exactly like the
                    // empty-mask guard above — an existing structure's
                    // contours are never replaced with an empty result.
                    texture.delete();
                    enqueueSnackbar(
                        intl.formatMessage({ id: 'thresholdSeg.emptyResult' }),
                        { variant: 'error' }
                    );
                    return;
                }
                ownedTexture = texture;

                // A rejected destination (approved mid-run, vanished, or a
                // creation the domain refuses — e.g. DuplicateExternalRoiError
                // reported by getOrCreateDestination's generic failure
                // snackbar) leaves nothing written; only the texture must
                // be released, which the finally-adjacent catch handles.
                dest = await getOrCreateDestination(outputRoiNumber, newName, {
                    interpretedType: preset.output.interpretedType,
                    generation: {
                        algorithm: RoiGenerationAlgorithmType.AUTOMATIC,
                        description: describeGeneration(params),
                    },
                    // Preset structure code (e.g. Varian BODY), so a
                    // receiving TPS maps the structure to its template.
                    // Only stamped on creation — an existing structure's
                    // code is clinical metadata this operation must not
                    // clobber.
                    identificationCode: preset.output.identificationCode,
                    color,
                });
                if (!dest) {
                    texture.delete();
                    ownedTexture = undefined;
                    return;
                }
                if (outputRoiNumber === null) {
                    createdRoiNumber = dest.roiNumber;
                }

                // The destination's color BEFORE this apply, captured for
                // the no-op-recolor check below (replaceTexture does not
                // touch it, but read it first for clarity).
                const priorColor = dest.colorRgb8;
                dest.replaceTexture(texture, BODY_CONTOUR_OPERATION);
                // The overwrite path also carries freshly generated
                // contours, so it gets the AUTOMATIC stamp, the run's
                // parameter record as generation description, and the
                // chosen color — the create path applied all three at
                // creation.
                // The contour-modified handler exempts
                // BODY_CONTOUR_OPERATION writes from edit-demotion, so the
                // stamp survives regardless of event ordering. Known
                // limitation (shared with every operation's property
                // writes): contour undo restores the pixels but not these
                // properties.
                if (outputRoiNumber !== null) {
                    const domainRoi = environment.structureSet.parent.rois.find(
                        (r) => r.roiNumber === dest!.roiNumber
                    );
                    if (domainRoi) {
                        session.context.tracker.command(() => {
                            domainRoi.setGenerationAlgorithm(
                                RoiGenerationAlgorithmType.AUTOMATIC
                            );
                            domainRoi.dicomRoi.roiGenerationDescription =
                                describeGeneration(params);
                        });
                    }
                    // Publish the color only when the user actually chose
                    // a different one: the panel seeds its swatch from
                    // the existing EXTERNAL, so an untouched Apply must
                    // not record a spurious 'user' color change.
                    const colorChanged =
                        priorColor.R !== color.R ||
                        priorColor.G !== color.G ||
                        priorColor.B !== color.B;
                    if (colorChanged) {
                        await domainEventBus.publish(
                            new RoiColorChangeRequestedEvent(
                                dest.roiNumber,
                                color,
                                'user'
                            )
                        );
                    }
                }
                // The result is written and stamped: from here on the new
                // structure is the user's, so a later throw (view update,
                // notification, panel close) must never roll it back.
                createdRoiNumber = null;
                updateView();
                enqueueSnackbar(
                    intl.formatMessage(
                        { id: 'thresholdSeg.success' },
                        { name: dest.name }
                    ),
                    { variant: 'success' }
                );
                onSuccess();
            } catch (e) {
                // Release the texture unless replaceTexture already handed
                // it to the ROI (it assigns before the undo snapshot, which
                // can itself throw — deleting then would free a texture the
                // ROI now owns).
                if (ownedTexture && dest?.texture !== ownedTexture) {
                    ownedTexture.delete();
                }
                // Roll back a structure THIS apply created: the failure
                // happened after creation, so leaving it behind would add
                // an empty ROI stamped AUTOMATIC that the user never
                // asked for and the error snackbar does not mention. A
                // pre-existing destination is never deleted — the
                // overwrite path leaves createdRoiNumber null. The shared
                // helper logs (rather than silently skips) a rollback a
                // missing structure-set UID makes impossible, and only
                // ever logs a rollback failure — the original error below
                // is what the user needs surfaced.
                await rollbackCreatedRois(
                    environment?.structureSet?.sopInstanceUID,
                    createdRoiNumber !== null ? [createdRoiNumber] : []
                );
                console.error('Threshold segmentation apply error:', e);
                enqueueSnackbar(
                    intl.formatMessage(
                        { id: 'thresholdSeg.failed' },
                        { error: getErrorMessage(e) }
                    ),
                    { variant: 'error' }
                );
            } finally {
                setIsProcessing(false);
            }
        },
        [
            environment,
            image,
            isProcessing,
            getOrCreateDestination,
            updateView,
            onSuccess,
            consumePreview,
            maskCache,
            eligibility,
            describeGeneration,
            intl,
            session,
        ]
    );

    return { isProcessing, handleApply };
}
